import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import { AppError } from "../AppError";
import { getTracingHeaders } from "../tracing";
import type { paths } from "./oas";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isClientRoute = (url: string) => {
  try {
    return new URL(url).pathname.includes('/client/');
  } catch {
    return url.includes('/client/');
  }
};

const getErrorBodyMessage = (data: unknown) => {
  if (typeof data === 'string') return data;
  if (!isRecord(data)) return undefined;

  const nestedData = data['data'];
  const nestedMessage = isRecord(nestedData) ? nestedData['message'] : undefined;
  const message = nestedMessage ?? data['message'];

  return typeof message === 'string' && message.length > 0 ? message : undefined;
};

const getUnifiedApiErrorMessage = (request: Request, response: Response, data: unknown) => {
  const statusText = response.statusText ? ` ${response.statusText}` : '';
  const bodyMessage = getErrorBodyMessage(data);
  const detail = bodyMessage ? `: ${bodyMessage}` : '';

  return `Unified API ${request.method} ${request.url} failed with ${response.status}${statusText}${detail}`;
};

const getUnifiedApiNetworkErrorMessage = (request: Request, cause: unknown) => {
  const causeMessage = cause instanceof Error ? `: ${cause.message}` : '';

  return `Unified API ${request.method} ${request.url} failed before a response was available${causeMessage}`;
};

const readUnifiedApiErrorData = async (response: Response) => {
  const text = await response.clone().text().catch(() => '');

  if (!text) return undefined;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export class UnifiedApiError extends Error {
  readonly data: unknown;
  readonly request: Request;
  readonly response: Response;
  readonly status: number;
  readonly statusText: string;

  constructor(request: Request, response: Response, data: unknown) {
    super(getUnifiedApiErrorMessage(request, response, data));
    this.name = 'UnifiedApiError';
    this.data = data;
    this.request = request;
    this.response = response;
    this.status = response.status;
    this.statusText = response.statusText;
  }
}

export class UnifiedApiNetworkError extends Error {
  readonly cause: unknown;
  readonly request: Request;

  constructor(request: Request, cause: unknown) {
    super(getUnifiedApiNetworkErrorMessage(request, cause));
    this.name = 'UnifiedApiNetworkError';
    this.cause = cause;
    this.request = request;
  }
}

export const isUnifiedApiError = (error: unknown): error is UnifiedApiError | UnifiedApiNetworkError =>
  error instanceof UnifiedApiError || error instanceof UnifiedApiNetworkError;

// Slightly above the API gateway's ~29s integration timeout so the server's answer
// (even a 504) wins the race, while a transport-level hang still gets converted into
// an error instead of wedging the UI forever.
const REQUEST_TIMEOUT_MS = 35_000;

export const unifiedApiClient = createFetchClient<paths>({
  baseUrl: import.meta.env['VITE_UNIFIED_API_URL'],
  credentials: 'include',
  // Late-bound transport: openapi-fetch otherwise captures globalThis.fetch once at
  // module init, which pins the client to whatever fetch existed first and hides it
  // from runtime patches (MirageJS mock servers in storybook, RUM/test instrumentation).
  fetch: (request) => globalThis.fetch(request, { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) }),
});

const getRefreshUrl = () =>
  `${String(import.meta.env['VITE_UNIFIED_API_URL'] ?? '').replace(/\/+$/, '')}/public/token/refresh`;

// Single-flight: concurrent 401s share one refresh call instead of racing the rotation.
// A failed refresh also starts a cooldown: sequential 401s from independent call sites
// (route guards, panels) otherwise each fire their own doomed refresh on anonymous pages.
const REFRESH_FAILURE_COOLDOWN_MS = 15_000;
let refreshSessionPromise: Promise<boolean> | null = null;
let refreshFailedAt = 0;

const refreshSession = () => {
  if (Date.now() - refreshFailedAt < REFRESH_FAILURE_COOLDOWN_MS) return Promise.resolve(false);

  refreshSessionPromise ??= fetch(getRefreshUrl(), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  })
    .then((response) => response.ok)
    .catch(() => false)
    .then((ok) => {
      if (!ok) refreshFailedAt = Date.now();
      return ok;
    })
    .finally(() => {
      refreshSessionPromise = null;
    });

  return refreshSessionPromise;
};

// A request's body is consumed by the time onResponse sees a 401, so retries replay a clone
// stashed at request time. The retry goes through plain fetch (not the middleware chain), so it
// cannot recurse into another refresh.
const retryableRequests = new WeakMap<Request, Request>();

const retryAfterRefresh = async (request: Request) => {
  const retryRequest = retryableRequests.get(request);
  if (!retryRequest || !(await refreshSession())) return undefined;

  try {
    return await fetch(retryRequest, { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });
  } catch {
    return undefined;
  }
};

unifiedApiClient.use({
  onRequest: ({ request }) => {
    const tracingHeaders = getTracingHeaders();
    Object.entries(tracingHeaders).forEach(([name, value]) => request.headers.set(name, value));
    if (isClientRoute(request.url)) retryableRequests.set(request, request.clone());
    return request;
  },
  onResponse: async ({ request, response }) => {
    if (response.status < 400) return undefined;

    // An expired access token 401s: refresh the session cookies once (single-flight across
    // concurrent requests) and replay the original request before giving up on the session.
    if (response.status === 401 && isClientRoute(request.url)) {
      const retried = await retryAfterRefresh(request);

      if (retried && retried.status < 400) return retried;
      if (retried && retried.status !== 401 && retried.status !== 403) {
        throw new UnifiedApiError(request, retried, await readUnifiedApiErrorData(retried));
      }

      throw AppError.Unauthenticated;
    }

    // Authed application routes map 401/403 to AppError.Unauthenticated for parity with the
    // butterfly axios client, so the existing error boundaries re-trigger login. Public routes
    // (otp, prove, create) keep surfacing UnifiedApiError so they can show their own messaging.
    if (response.status === 403 && isClientRoute(request.url)) {
      throw AppError.Unauthenticated;
    }

    throw new UnifiedApiError(request, response, await readUnifiedApiErrorData(response));
  },
  onError: ({ request, error }) => new UnifiedApiNetworkError(request, error),
});

export const unifiedApi = createClient(unifiedApiClient);
